import {
  Cartesian2,
  Cartesian3,
  Check,
  defined,
  destroyObject,
  DeveloperError,
  Frozen,
  IntersectionTests,
  Matrix4,
  Plane,
  Ray,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  SceneTransforms,
  Cesium3DTileset,
  GlobeSurfaceTileProvider,
  Model,
  PrimitiveCollection,
  SceneMode,
} from 'cesium';
import ClippingPlanePrimitive from "./ClippingPlanePrimitive.js";

const planeScratch = new Plane(Cartesian3.UNIT_X, 0);
const scalarScratch = new Cartesian3();
const positionScratch = new Cartesian3();
const normalScratch = new Cartesian3();
const inverseTransformScratch1 = new Matrix4();
const inverseTransformScratch2 = new Matrix4();
const cart3Scratch1 = new Cartesian3();
const cart3Scratch2 = new Cartesian3();
const cart3Scratch3 = new Cartesian3();
const cart3Scratch4 = new Cartesian3();
const cart3Scratch5 = new Cartesian3();
const rayScratch = new Ray();
const matrixScratch1 = new Matrix4();
const screen2Scratch1 = new Cartesian2();
const screen2Scratch2 = new Cartesian2();
const screen2Scratch3 = new Cartesian2();

function computeOrigin(transform, origin, plane, result) {
  let adjustedPlane = Plane.clone(plane, planeScratch);
  adjustedPlane.distance = 0;

  adjustedPlane = Plane.transform(adjustedPlane, transform, adjustedPlane);
  return Cartesian3.subtract(
    origin,
    Cartesian3.multiplyByScalar(
      adjustedPlane.normal,
      Plane.getPointDistance(adjustedPlane, origin),
      scalarScratch,
    ),
    result,
  );
}

function movePlane(transform, origin, plane) {
  let adjustedPlane = Plane.clone(plane, planeScratch);
  adjustedPlane.distance = 0;
  adjustedPlane = Plane.transform(adjustedPlane, transform, adjustedPlane);
  plane.distance = -Plane.getPointDistance(adjustedPlane, origin);
}

/**
 * Creates visual clipping planes and mouse handlers for adjusting the clipping planes added to a {@link Cesium3DTileset}, {@link Model} or {@link Globe}.
 *
 * @alias ClippingPlanesEditor
 * @ionsdk
 * @constructor
 *
 * @param {Object} options An object with the following properties:
 * @param {Scene} options.scene The scene.
 * @param {ClippingPlaneCollection} options.clippingPlanes A clipping plane collection that has been set for a Cesium3DTileset, Model or Globe.
 * @param {Cartesian3} [options.origin] The position the visual clipping planes are relative to.  If not provided, a position will be computed from the modelMatrix or the primitive bounding sphere.
 * @param {Cartesian2} [options.planeSizeInMeters] The width and height of the clipping planes in meters. If not provided, size will be computed based on the primitive bounding sphere radius, or 200.0 for terrain clipping.
 * @param {Boolean} [options.movePlanesToOrigin=true] If true, ClippingPlanesEditor recomputes the plane distance to move the clipping planes to the origin.  Otherwise the original plane distance is unaltered.
 * @param {Cartesian2} [options.pixelSize=Cesium.Cartesian2(100, 100)] The size of the clipping planes in pixels of screen space in each direction. This overrides options.planeSizeInMeters. To disable fixed screen-space size for either the x- or y-direction, set the corresponding component(s) to 0
 * @param {Cartesian2} [options.maximumSizeInMeters=Cesium.Cartesian2(Infinity, Infinity)] The maximum size of the clipping planes in meters when fixed screen-space size is enabled. To disable this size limit for either the x- or y-direction, set the corresponding component(s) to Infinity
 *
 * @demo <a href="/Apps/Sandcastle/index.html?src=Clipping%20Planes%20Editor.html">Cesium Sandcastle Clipping Planes Editor Demo</a>
 * @example
 * // Attach 4 clipping planes to a tileset
 * tileset.clippingPlanes = new IonSdkMeasurements.ClippingPlaneCollection({
 *  planes : [
 *    new IonSdkMeasurements.ClippingPlane(new Cesium.Cartesian3(1.0, 0.0, 0.0), 100),
 *    new IonSdkMeasurements.ClippingPlane(new Cesium.Cartesian3(-1.0, 0.0, 0.0), 100),
 *    new IonSdkMeasurements.ClippingPlane(new Cesium.Cartesian3(0.0, 1.0, 0.0), 100),
 *    new IonSdkMeasurements.ClippingPlane(new Cesium.Cartesian3(0.0, -1.0, 0.0), 100),
 *  ],
 *  unionClippingRegions: true,
 *  edgeWidth : 1.0
 * });
 * // Create and activate the editor to visualize and move the planes.
 * var clippingPlanesEditor = new IonSdkMeasurements.ClippingPlanesEditor({
 *  scene: viewer.scene,
 *  clippingPlanes: tileset.clippingPlanes,
 *  movePlanesToOrigin: false
 * });
 * clippingPlanesEditor.activate();
 */
function ClippingPlanesEditor(options) {
  const scene = options.scene;
  const clippingPlanes = options.clippingPlanes;

  //>>includeStart('debug', pragmas.debug);
  Check.defined("options.scene", scene);
  Check.defined("options.clippingPlanes", clippingPlanes);
  //>>includeEnd('debug');

  const primitives = scene.primitives.add(new PrimitiveCollection());

  const that = this;
  this._removeAddEventListener = clippingPlanes.planeAdded.addEventListener(
    function () {
      that._addPlane();
    },
    this,
  );
  this._removeRemoveEventListener =
    clippingPlanes.planeRemoved.addEventListener(
      ClippingPlanesEditor.prototype._removePlane,
      this,
    );

  this._planeSizeInMeters = options.planeSizeInMeters;
  this._scene = scene;
  this._sseh = new ScreenSpaceEventHandler(scene.canvas);
  this._clippingPlanes = clippingPlanes;
  this._primitives = primitives;
  this._origin = options.origin;
  this._movePlanesToOrigin = options.movePlanesToOrigin ?? true;
  this._primitiveOptions = options.primitiveOptions ?? Frozen.EMPTY_OBJECT;
  this._active = false;
  this._pickedPlane = undefined;
  this._hoverPlane = undefined;
  this._draggingPlane = new Plane(Cartesian3.UNIT_X, 0);
  this._pixelSize = options.pixelSize;
  this._maximumSizeInMeters = options.maximumSizeInMeters;

  this._target = undefined;
  this._transform = Matrix4.clone(Matrix4.IDENTITY);
  this._createPlanes();
}

Object.defineProperties(ClippingPlanesEditor.prototype, {
  /**
   * Gets whether the clipping plane tool is active.
   * @type {Boolean}
   * @memberof ClippingPlanesEditor.prototype
   * @readonly
   */
  active: {
    get: function () {
      return this._active;
    },
  },
  /**
   * Gets the scene.
   * @type {Scene}
   * @memberof ClippingPlanesEditor.prototype
   * @readonly
   */
  scene: {
    get: function () {
      return this._scene;
    },
  },
  /**
   * Gets the clipping plane collection.
   * @type {ClippingPlaneCollection}
   * @memberof ClippingPlanesEditor.prototype
   * @readonly
   */
  clippingPlanes: {
    get: function () {
      return this._clippingPlanes;
    },
  },
});

/**
 * Activates mouse handlers.
 */
ClippingPlanesEditor.prototype.activate = function () {
  this._active = true;

  const primitives = this._primitives;
  for (let i = 0; i < primitives.length; i++) {
    primitives.get(i).show = true;
  }
  const sseh = this._sseh;

  sseh.setInputAction(
    this._handleLeftDown.bind(this),
    ScreenSpaceEventType.LEFT_DOWN,
  );
  sseh.setInputAction(
    this._handleLeftUp.bind(this),
    ScreenSpaceEventType.LEFT_UP,
  );
  sseh.setInputAction(
    this._handleMouseMove.bind(this),
    ScreenSpaceEventType.MOUSE_MOVE,
  );
};

/**
 * Deactivates the mouse handlers.
 */
ClippingPlanesEditor.prototype.deactivate = function () {
  const primitives = this._primitives;
  for (let i = 0; i < primitives.length; i++) {
    primitives.get(i).show = false;
  }

  const sseh = this._sseh;
  sseh.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
  sseh.removeInputAction(ScreenSpaceEventType.LEFT_UP);
  sseh.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);

  this._active = false;
};

/**
 * @private
 * Adds all clipping planes from the collection
 */
ClippingPlanesEditor.prototype._addAllPlanes = function () {
  const clippingPlanes = this._clippingPlanes;
  for (let i = 0; i < clippingPlanes.length; i++) {
    this._addPlane(clippingPlanes.get(i));
  }
};

/**
 * @private
 * Sets up transform and origin for a target, then adds planes
 */
ClippingPlanesEditor.prototype._initializeForTarget = function (transform, originSource) {
  this._transform = transform;
  if (!defined(this._origin)) {
    this._origin = Cartesian3.clone(originSource);
  }
  this._addAllPlanes();
};

/**
 * @private
 */
ClippingPlanesEditor.prototype._createPlanes = function () {
  const clippingPlanes = this._clippingPlanes;
  const target = clippingPlanes.owner;

  // Wait for target to be assigned (e.g., model entity)
  if (typeof target === "undefined") {
    const removeListener = this._scene.preRender.addEventListener(() => {
      removeListener();
      if (!this.isDestroyed()) {
        this._createPlanes();
      }
    });
    return;
  }

  this._target = target;

  // Handle Cesium3DTileset
  if (target instanceof Cesium3DTileset) {
    this._initializeForTarget(target.clippingPlanesOriginMatrix, target.boundingSphere.center);
    return;
  }

  // Handle Model (may need to wait for ready)
  if (target instanceof Model) {
    const initModel = () => {
      this._initializeForTarget(target.modelMatrix, target.boundingSphere.center);
    };

    if (target.ready) {
      initModel();
      return;
    }

    const removeListener = target.readyEvent.addEventListener(() => {
      removeListener();
      if (!this.isDestroyed()) {
        initModel();
      }
    });
    return;
  }

  // Handle GlobeSurfaceTileProvider
  if (target instanceof GlobeSurfaceTileProvider) {
    const originFromMatrix = Matrix4.getTranslation(clippingPlanes.modelMatrix, new Cartesian3());
    this._initializeForTarget(clippingPlanes.modelMatrix, originFromMatrix);
    return;
  }

  //>>includeStart('debug', pragmas.debug);
  throw new DeveloperError(
    "ClippingPlanesEditor is incompatible with the object owning options.clippingPlanes",
  );
  //>>includeEnd('debug');
};

/**
 * @private
 */
ClippingPlanesEditor.prototype._addPlane = function (clippingPlane) {
  const that = this;
  const movePlanesToOrigin = this._movePlanesToOrigin;
  const primitives = this._primitives;
  let planePosition;
  let planeSizeInMeters = this._planeSizeInMeters;
  const pixelSize = this._pixelSize;
  const maximumSizeInMeters = this._maximumSizeInMeters;
  const primitiveOptions = this._primitiveOptions;

  const target = that._target;
  const origin = that._origin;
  const transform = that._transform;
  const inverseTransform = Matrix4.inverse(transform, inverseTransformScratch1);
  if (Matrix4.equals(transform, Matrix4.IDENTITY)) {
    planePosition = origin;
    if (movePlanesToOrigin) {
      clippingPlane.distance = 0;
    }
  } else {
    planePosition = computeOrigin(
      transform,
      origin,
      clippingPlane,
      positionScratch,
    );
    if (movePlanesToOrigin) {
      movePlane(transform, origin, clippingPlane);
    }
  }

  if (!defined(planeSizeInMeters)) {
    planeSizeInMeters = that._computePlaneSize(target);
  }

  const localOrigin = Matrix4.multiplyByPoint(
    inverseTransform,
    planePosition,
    cart3Scratch5,
  );
  const localTransform = Matrix4.fromTranslation(localOrigin);

  primitives.add(
    new ClippingPlanePrimitive({
      transform: transform,
      localTransform: localTransform,
      clippingPlane: clippingPlane,
      show: that._active,
      size: planeSizeInMeters,
      disableDepthFail: primitiveOptions.disableDepthFail,
      outlineColor: primitiveOptions.outlineColor,
      frontColor: primitiveOptions.frontColor,
      backColor: primitiveOptions.backColor,
      highlightColor: primitiveOptions.highlightColor,
      pixelSize: pixelSize,
      maximumSizeInMeters: maximumSizeInMeters,
    }),
  );
};

/**
 * @private
 */
ClippingPlanesEditor.prototype._computePlaneSize = function (target) {
  if (target instanceof GlobeSurfaceTileProvider) {
    return new Cartesian2(200, 200);
  }

  const halfRadius = target.boundingSphere.radius * 0.5;
  return new Cartesian2(halfRadius, halfRadius);
};

/**
 * @private
 */
ClippingPlanesEditor.prototype._removePlane = function (plane) {
  const primitives = this._primitives;
  for (let i = 0; i < primitives.length; i++) {
    const planePrimitive = primitives.get(i);
    if (planePrimitive.clippingPlane === plane) {
      primitives.remove(planePrimitive);
      break;
    }
  }
};

/**
 * @private
 */
ClippingPlanesEditor.prototype._handleLeftDown = function (movement) {
  const scene = this._scene;
  if (scene.mode !== SceneMode.SCENE3D) {
    return;
  }
  const pickedObject = scene.pick(movement.position);
  if (
    defined(pickedObject) &&
    defined(pickedObject.id) &&
    pickedObject.id.plane instanceof ClippingPlanePrimitive
  ) {
    const pickedPlane = pickedObject.id.plane;
    pickedPlane.highlight(true);

    const clippingPlaneNormal = pickedPlane._normalWC;
    let planeNormal = Cartesian3.cross(
      clippingPlaneNormal,
      scene.camera.directionWC,
      normalScratch,
    );
    planeNormal = Cartesian3.cross(
      clippingPlaneNormal,
      planeNormal,
      planeNormal,
    );
    planeNormal = Cartesian3.normalize(planeNormal, planeNormal);

    this._draggingPlane = Plane.fromPointNormal(
      pickedPlane.centerPosition,
      planeNormal,
      this._draggingPlane,
    );
    scene.screenSpaceCameraController.enableInputs = false;
    this._pickedPlane = pickedPlane;
    if (scene.requestRenderMode) {
      scene.requestRender();
    }
  }
};

/**
 * @private
 */
ClippingPlanesEditor.prototype._handleLeftUp = function () {
  const scene = this._scene;
  if (scene.mode !== SceneMode.SCENE3D) {
    return;
  }
  const pickedPlane = this._pickedPlane;
  if (defined(pickedPlane)) {
    pickedPlane.highlight(false);
    this._pickedPlane = undefined;
    if (scene.requestRenderMode) {
      scene.requestRender();
    }
  }
  scene.screenSpaceCameraController.enableInputs = true;
};

/**
 * @private
 */
ClippingPlanesEditor.prototype._handleMouseMove = function (movement) {
  const scene = this._scene;
  if (scene.mode !== SceneMode.SCENE3D) {
    return;
  }
  const pickedPlane = this._pickedPlane;
  if (defined(pickedPlane)) {
    const draggingPlane = this._draggingPlane;
    // Base intersection at start position
    let pickRay = scene.camera.getPickRay(movement.startPosition, rayScratch);
    const startDragPos = IntersectionTests.rayPlane(pickRay, draggingPlane, cart3Scratch1);
    if (!defined(startDragPos)) return;

    // Project plane normal to screen space to get 2D drag axis
    const P = pickedPlane.centerPosition;
    const n = pickedPlane._normalWC;
    const p0 = SceneTransforms.worldToWindowCoordinates(scene, P, screen2Scratch1);
    const p1 = SceneTransforms.worldToWindowCoordinates(scene, Cartesian3.add(P, n, cart3Scratch5), screen2Scratch2);
    if (!p0 || !p1) return;
    const nsx = p1.x - p0.x;
    const nsy = p1.y - p0.y;
    const nsLen = Math.hypot(nsx, nsy) || 1.0;
    const nsux = nsx / nsLen;
    const nsuy = nsy / nsLen;

    // Screen delta along normal projection (signed pixels)
    const sdx = movement.endPosition.x - movement.startPosition.x;
    const sdy = movement.endPosition.y - movement.startPosition.y;
    const sPixels = sdx * nsux + sdy * nsuy;

    // Sample meters-per-pixel along normal by taking a 1px step
    screen2Scratch3.x = movement.startPosition.x + nsux;
    screen2Scratch3.y = movement.startPosition.y + nsuy;
    pickRay = scene.camera.getPickRay(screen2Scratch3, rayScratch);
    const samplePos = IntersectionTests.rayPlane(pickRay, draggingPlane, cart3Scratch2);
    if (!defined(samplePos)) return;
    const dSample = Cartesian3.subtract(samplePos, startDragPos, cart3Scratch3);
    const metersPerPixel = Cartesian3.dot(dSample, n);

    // Signed displacement along the plane normal in meters
    const signedDelta = metersPerPixel * sPixels;

    // Translate along the plane's local normal direction
    const worldTransform = Matrix4.multiply(
      pickedPlane.transform,
      pickedPlane.localTransform,
      matrixScratch1,
    );
    const inverseWorld = Matrix4.inverse(worldTransform, inverseTransformScratch2);
    const localNormal = Matrix4.multiplyByPointAsVector(inverseWorld, n, cart3Scratch4);
    Cartesian3.normalize(localNormal, localNormal);
    const localDelta = Cartesian3.multiplyByScalar(localNormal, signedDelta, cart3Scratch5);
    Matrix4.multiplyByTranslation(pickedPlane.localTransform, localDelta, pickedPlane.localTransform);

    if (scene.requestRenderMode) {
      scene.requestRender();
    }
  } else {
    if (defined(this._hoverPlane)) {
      this._hoverPlane.highlight(false);
    }
    const pickedObject = scene.pick(movement.endPosition);
    if (
      defined(pickedObject) &&
      defined(pickedObject.id) &&
      pickedObject.id.plane instanceof ClippingPlanePrimitive
    ) {
      const hoverPlane = pickedObject.id.plane;
      hoverPlane.highlight(true);
      this._hoverPlane = hoverPlane;
    }
  }
};

/**
 * Resets the clipping planes to their start position.
 */
ClippingPlanesEditor.prototype.reset = function () {
  const primitives = this._primitives;
  for (let i = 0; i < primitives.length; i++) {
    primitives.get(i).reset();
  }
};

/**
 * @returns {Boolean} true if the object has been destroyed, false otherwise.
 */
ClippingPlanesEditor.prototype.isDestroyed = function () {
  return false;
};

/**
 * Destroys the clipping planes tool.
 */
ClippingPlanesEditor.prototype.destroy = function () {
  this.deactivate();
  this._sseh.destroy();
  this._scene.primitives.remove(this._primitives);
  this._removeAddEventListener();
  this._removeRemoveEventListener();

  return destroyObject(this);
};

/**
 * Sets a rotation around the Z axis (in radians) for both the clipping
 * collection (affecting actual clipping) and the visual plane primitives.
 * @param {Number} radians Rotation in radians.
 */
ClippingPlanesEditor.prototype.setRotationZ = function (radians) {
  try {
    const rot3 = Matrix3.fromRotationZ(radians);
    const rot4 = Matrix4.fromRotationTranslation(rot3, new Cartesian3(0, 0, 0), new Matrix4());

    // Update visual plane primitives local transforms, rotating about their local origin
    const primitives = this._primitives;
    for (let i = 0; i < primitives.length; i++) {
      const planePrimitive = primitives.get(i);
      // Rotate around the plane's local origin: T(origin) * Rz
      planePrimitive._localTransform = Matrix4.multiply(
        planePrimitive._initialLocalTransform,
        rot4,
        planePrimitive._localTransform,
      );
      planePrimitive._update = true;
    }

    if (this._scene && this._scene.requestRenderMode) {
      this._scene.requestRender();
    }
  } catch (e) { /* ignore */ }
};
export default ClippingPlanesEditor;
